import { assert } from '@ovpn-ui/utils';
import { call, takeEvery, takeLatest, getContext } from 'redux-saga/effects';
const DefaultValidationErrorCode = '2-12-1';
export const RequestBadResponseActionType = 'REQUEST_BAD_RESPONSE';
export function requestBadResponseAction(payload) {
    return {
        type: RequestBadResponseActionType,
        payload,
    };
}
const matchFailureAction = (action) => {
    if (action.type.indexOf('@@PROMPT') !== -1) {
        return false;
    }
    return action.type.indexOf('FAILURE') !== -1;
};
function injectParams(message = '', params = {}) {
    return Object.entries(params).reduce((acc, [key, val]) => acc.replace(new RegExp(`{${key}}`, 'g'), val), message);
}
/**
 * Creates a `FOO/FAILURE` event watcher which tries to parse error from BE response
 * and call onError fn, which is usually a toast react component.
 *
 * It uses redux-saga context for configuration. You can set this once during store init, like `createSagaMiddleware({ context: {...} })`
 * or dynamically using `yield setContext('opt', value)`
 *
 * Context options are:
 *
 * -    onError: `(error: string) => void` function that is called to display error message produced by this worker. It may be a react-tostify toast.error fn or anything else.
 * -    errorMessages: object map of { [errorCode: string]: errorMessage, ... }
 * -    requestValidationErrorCode: string. Validation Error code. By default it is '2-12-1', but your BE may use different one.
 * -    useErrorTemplates: bool. If true - parses dynamic message templates and injects values from BE fieldErrors response.
 */
export default function* () {
    const onError = yield getContext('onError');
    const errorMessages = yield getContext('errorMessages');
    const useErrorTemplates = yield getContext('useErrorTemplates');
    const requestValidationErrorCode = (yield getContext('requestValidationErrorCode')) ||
        DefaultValidationErrorCode;
    assert(!!onError && typeof onError === 'function', 'You are using errorSaga but not passing onError function to saga context.');
    assert(!!errorMessages && Object.keys(errorMessages).length > 0, 'You are using errorSaga but not passing errorMessages map to saga context.');
    const parseFieldErrors = (fieldErrors) => Object.values(fieldErrors)
        .flatMap(t => t)
        .map((t) => {
        if (typeof t === 'string') {
            return t;
        }
        if (useErrorTemplates) {
            return injectParams(errorMessages[t.errorCode], t.params || {});
        }
        return t.message;
    })
        .join('\n');
    function getMessage(error) {
        const hasFieldErrors = error.fieldErrors && Object.keys(error.fieldErrors).length > 0;
        if (Reflect.has(errorMessages, error.errorCode)) {
            if (hasFieldErrors &&
                error.errorCode === requestValidationErrorCode) {
                return parseFieldErrors(error.fieldErrors);
            }
            return errorMessages[error.errorCode];
        }
        if (hasFieldErrors) {
            return parseFieldErrors(error.fieldErrors);
        }
        return `Internal error ${error.errorCode}`;
    }
    function* outputError(message) {
        yield call(onError, message);
    }
    function* errorHandlerSaga({ payload: error, }) {
        if (error === null || error === void 0 ? void 0 : error.errorCode) {
            const message = getMessage(error);
            if (message) {
                yield outputError(message);
            }
        }
        else if (error) {
            if (error.message) {
                yield outputError(error.message);
            }
            if (process.env.NODE_ENV === 'development') {
                console.error(error); // eslint-disable-line
            }
        }
    }
    function* serverErrorHandlerSaga() {
        yield outputError('Connection failed. Check back soon.');
    }
    yield takeEvery(matchFailureAction, errorHandlerSaga);
    yield takeLatest(RequestBadResponseActionType, serverErrorHandlerSaga);
}
